/** Модели и статусы рекламных кампаний (админка). */

/** Жизненный цикл кампании (совпадает с AdCampaignStatus на бэкенде). */
export enum AdCampaignStatus {
    /** Черновик */
    Draft = 0,
    /** Активна — принимает атрибуцию */
    Active = 1,
    /** Пауза */
    Paused = 2,
    /** Завершена */
    Finished = 3,
}

export const AdCampaignStatusLabels: Record<AdCampaignStatus, string> = {
    [AdCampaignStatus.Draft]: "Черновик",
    [AdCampaignStatus.Active]: "Активна",
    [AdCampaignStatus.Paused]: "Пауза",
    [AdCampaignStatus.Finished]: "Завершена",
};

/**
 * Допустимые переходы статусов (зеркало серверной AdCampaignStatusTransitions).
 * Draft → Active | Finished
 * Active → Paused | Finished
 * Paused → Active | Finished
 * Finished → ∅
 * В Draft после запуска вернуться нельзя.
 */
export function getAllowedAdCampaignStatuses(
    current: AdCampaignStatus,
    options?: { isCreate?: boolean }
): AdCampaignStatus[] {
    if (options?.isCreate) {
        return [AdCampaignStatus.Draft, AdCampaignStatus.Active];
    }

    switch (current) {
        case AdCampaignStatus.Draft:
            return [AdCampaignStatus.Draft, AdCampaignStatus.Active, AdCampaignStatus.Finished];
        case AdCampaignStatus.Active:
            return [AdCampaignStatus.Active, AdCampaignStatus.Paused, AdCampaignStatus.Finished];
        case AdCampaignStatus.Paused:
            return [AdCampaignStatus.Paused, AdCampaignStatus.Active, AdCampaignStatus.Finished];
        case AdCampaignStatus.Finished:
            return [AdCampaignStatus.Finished];
        default:
            return [current];
    }
}

/** Предустановленные каналы для utm_source / фильтрации. */
export const AD_CAMPAIGN_CHANNELS = [
    "Vk",
    "Telegram",
    "YouTube",
    "Influencer",
    "Google",
    "Yandex",
    "Other",
] as const;

/** Кампания с сервера, включая вычисленный TrackingUrl. */
export type AdCampaign = {
    id: number;
    name: string;
    /** Токен ?c= — не редактируется после создания */
    code: string;
    status: AdCampaignStatus;
    channel: string;
    description?: string | null;
    notes?: string | null;
    startAt: string;
    endAt?: string | null;
    promoCode?: string | null;
    landingUrl: string;
    /** Затраты в копейках */
    spendKopecks?: number | null;
    createdAt: string;
    createdByUserId?: number | string | null;
    updatedAt: string;
    /** LandingUrl + c + авто-UTM — копировать рекламщику */
    trackingUrl: string;
} & AdCampaignPromoBonuses;

/** Бонусы промокода кампании и лимит активаций; null — бонуса или лимита нет. */
export type AdCampaignPromoBonuses = {
    /** Комиссия на продажу по промокоду, % (вместо обычной), 0–100 */
    saleCommissionPercent?: number | null;
    /** Скидка на покупку, % (больше 0, до 100) */
    purchaseDiscountPercent?: number | null;
    /** Бонус к депозиту, % (больше 0, до 100) */
    depositBonusPercent?: number | null;
    /** Лимит активаций (от 1) */
    maxActivations?: number | null;
};

export type AdCampaignListResult = {
    total: number;
    items: AdCampaign[];
    page: number;
    count: number;
};

export type CreateAdCampaignRequest = {
    name: string;
    channel: string;
    landingUrl: string;
    startAt: string;
    endAt?: string | null;
    description?: string | null;
    notes?: string | null;
    promoCode?: string | null;
    spendKopecks?: number | null;
    status: AdCampaignStatus;
} & AdCampaignPromoBonuses;

/** Частичный update; clear* — явный сброс опциональных полей. */
export type UpdateAdCampaignRequest = {
    id: number;
    name?: string;
    channel?: string;
    landingUrl?: string;
    startAt?: string;
    endAt?: string | null;
    clearEndAt?: boolean;
    description?: string | null;
    notes?: string | null;
    promoCode?: string | null;
    clearPromoCode?: boolean;
    spendKopecks?: number | null;
    clearSpend?: boolean;
    status?: AdCampaignStatus;
    /** true — заменить все четыре поля бонусов значениями запроса (null — очистить) */
    updatePromoBonuses?: boolean;
} & AdCampaignPromoBonuses;
